// React Hooks
import { memo, useRef, useEffect, useState, useMemo } from 'react';
// PropTypes
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

// Custom hook to detect current theme mode
import { useAppTheme } from '../../hooks/useAppTheme.js';

const DEFAULT_RADIUS = [0.7, 2.7];
const DEFAULT_SPEED = [1, 3];
const DEFAULT_WIND = [-0.5, 2];



/**
 * SnowEffect Component (OffscreenCanvas + Web Worker Version)
 *
 * Runs the animation in a separate thread to ensure 60fps even when
 * the main thread is blocked (e.g. by heavy React rendering or input handling).
 *
 * @component
 * @param {object} props
 * @param {number} [props.baseDensity] - Pixels per particle. Default: 2500 (lower = denser)
 * @param {number} [props.minCount] - Minimum particles. Default: 30
 * @param {number} [props.maxCount] - Max particle limit. Default: 500
 * @param {number[]} [props.radius] - Particle size range. Default: [0.7, 2.7]
 * @param {number[]} [props.speed] - Fall speed range. Default: [1, 3]
 * @param {number[]} [props.wind] - Wind strength range. Default: [-0.5, 2]
 * @param {number} [props.windDirection] - 1 = right, -1 = left. Default: 1
 * @param {number} [props.zIndex] - Canvas z-index. Default: 1
 * @param {boolean} [props.frostVignette] - Show frost overlay. Default: false
 */
const SnowEffect = memo(({
	/* 
       Configuration:
       - baseDensity: How many pixels of screen area equal ONE particle. 
         (LOWER value = MORE snow / HIGHER density).
         Example: 1920x1080 = ~2 million pixels. 
         At 2500 density -> ~500 particles (soft snow).
         At 100 density -> 20,000 particles (blizzard - dangerous for performance!).
       - maxCount: Hard cap to prevent freezing on huge screens.
    */
	baseDensity = 2500,
	minCount = 30,
	maxCount = 500,
	radius = DEFAULT_RADIUS,
	speed = DEFAULT_SPEED,       
	wind = DEFAULT_WIND,        
	windDirection = 1,    
	zIndex = 1,                
	frostVignette = false 
}) => {
	const canvasRef = useRef(null);
    const workerRef = useRef(null);
    const configRef = useRef(null);
	const { isDarkMode } = useAppTheme();
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

	// Theme colors
	const snowColor = '255, 255, 255'; 

    // Config object to send to worker
    const config = useMemo(() => ({
        radius, speed, wind, windDirection, maxCount, minCount, baseDensity, snowColor,
        isDarkMode // Pass theme mode to specificy contrast logic
    }), [radius, speed, wind, windDirection, maxCount, minCount, baseDensity, snowColor, isDarkMode]);

    // Keep configRef in sync
    configRef.current = config;


	useEffect(() => {
		const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		setPrefersReducedMotion(motionQuery.matches);
		const handleMotionChange = (e) => setPrefersReducedMotion(e.matches);
		motionQuery.addEventListener('change', handleMotionChange);
		return () => motionQuery.removeEventListener('change', handleMotionChange);
	}, []);

	useEffect(() => {
        if (prefersReducedMotion || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const useWorker = 'transferControlToOffscreen' in canvas;

        // --- WORKER PATH ---
		if (useWorker) {
            // Check if this specific canvas instance was already transferred (Strict Mode check)
            if (canvas.dataset.transferred) {
                 // Silently trigger remount to get a fresh canvas
                 setRetryCount(prev => prev + 1);
                 return;
            }

            try {
                if (workerRef.current) workerRef.current.terminate();
                workerRef.current = new Worker(new URL('./snowWorker.js', import.meta.url));

                // Mark as transferred BEFORE calling the function to be safe.
                canvas.dataset.transferred = 'true'; 
                const offscreen = canvas.transferControlToOffscreen();
                const { innerWidth, innerHeight, devicePixelRatio: dpr = 1 } = window;

                workerRef.current.postMessage({
                    type: 'INIT',
                    payload: {
                        canvas: offscreen,
                        dimensions: { width: innerWidth * dpr, height: innerHeight * dpr },
                        dpr,
                        config: configRef.current,
                    },
                }, [offscreen]);

            } catch (error) {
                // DOMException: An attempt was made to use an object that is not, or is no longer, usable
                // This happens when React unmounts/remounts and the canvas is already detached.
                const isDetached = error.name === 'InvalidStateError' || 
                                   (error.message && error.message.includes('not, or is no longer, usable')) ||
                                   (error.message && error.message.includes('Cannot transfer'));
                
                if (isDetached) {
                     console.warn("SnowEffect: Canvas already detached. Forcing remount...", error);
                     // Force React to destroy and recreate the canvas DOM node
                     setRetryCount(prev => prev + 1);
                     return; // Skip fallback, wait for new canvas
                }
                
                console.error("SnowEffect: Worker initialization failed critically.", error);
            }
		} 
        
        // --- MOUSE TRACKING (High-Velocity Gust Effect) ---
        let lastMouseX = 0;
        let lastMouseY = 0;
        let smoothVX = 0;
        let smoothVY = 0;
        let lastMouseTime = 0;
        const SMOOTH_FACTOR = 0.85; // High = less filtering, more responsive to flicks

        const handleMouseMove = (e) => {
            if (!workerRef.current) return;
            
            const now = performance.now();
            const dt = now - lastMouseTime;
            if (dt < 16) return; // Limit to ~60fps for performance
            
            const dpr = window.devicePixelRatio || 1;
            const currentX = e.clientX * dpr;
            const currentY = e.clientY * dpr;
            
            // Calculate raw velocity (pixels per frame)
            const rawVX = lastMouseTime > 0 ? (currentX - lastMouseX) : 0;
            const rawVY = lastMouseTime > 0 ? (currentY - lastMouseY) : 0;
            
            // Low-pass filter (exponential smoothing)
            smoothVX = smoothVX + (rawVX - smoothVX) * SMOOTH_FACTOR;
            smoothVY = smoothVY + (rawVY - smoothVY) * SMOOTH_FACTOR;
            
            // Update state for next frame
            lastMouseX = currentX;
            lastMouseY = currentY;
            lastMouseTime = now;

            workerRef.current.postMessage({
                type: 'MOUSE_MOVE',
                payload: {
                    x: currentX,
                    y: currentY,
                    vx: smoothVX,
                    vy: smoothVY,
                    active: true
                }
            });
        };

        const handleMouseOut = () => {
            if (workerRef.current) {
                workerRef.current.postMessage({
                    type: 'MOUSE_MOVE',
                    payload: { active: false, vx: 0, vy: 0 }
                });
            }
            // Reset smoothing state
            smoothVX = 0;
            smoothVY = 0;
        };

        const handleResize = () => {
             if (workerRef.current) {
                const dpr = window.devicePixelRatio || 1;
                workerRef.current.postMessage({
                    type: 'RESIZE',
                    payload: {
                        dimensions: { 
                            width: window.innerWidth * dpr, 
                            height: window.innerHeight * dpr 
                        },
                        dpr
                    }
                });
             }
        };

        const handleVisibilityChange = () => {
            if (!workerRef.current) return;
            if (document.hidden) {
                workerRef.current.postMessage({ type: 'STOP' });
            } else {
                workerRef.current.postMessage({ type: 'START' });
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseleave', handleMouseOut);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseleave', handleMouseOut);
            if (workerRef.current) {
                workerRef.current.terminate(); 
                workerRef.current = null;
            }
        };
	}, [prefersReducedMotion, retryCount]); // config changes handled by separate UPDATE_CONFIG effect


    // Update config in worker when props change
    useEffect(() => {
        if (!workerRef.current) return;

        // Defer update to next frame to allow main thread to handle theme switch CSS transitions first
        const frameId = requestAnimationFrame(() => {
             if (workerRef.current) {
                workerRef.current.postMessage({
                    type: 'UPDATE_CONFIG',
                    payload: config
                });
             }
        });

        return () => cancelAnimationFrame(frameId);
    }, [config]);


	if (prefersReducedMotion) return null;

	return (
		<>
			{/* Start Overlay: Frost Vignette */}
			{frostVignette && (
				<Box
					sx={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						boxShadow: 'inset 0 0 100px 30px rgba(200, 220, 255, 0.3)',
						zIndex: zIndex + 1,
						pointerEvents: 'none',
					}}
				/>
			)}
			{/* End Overlay */}

			{/* Snow Particle Canvas */}
			<Box
				component="canvas"
				ref={canvasRef}
				key={retryCount}
				sx={{
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					pointerEvents: 'none', // Must be none to allow clicks through
					zIndex: zIndex,
				}}
			/>
		</>
	);
});


SnowEffect.propTypes = {
	baseDensity: PropTypes.number,
	minCount: PropTypes.number,
	maxCount: PropTypes.number,
	radius: PropTypes.arrayOf(PropTypes.number),
	speed: PropTypes.arrayOf(PropTypes.number),
	wind: PropTypes.arrayOf(PropTypes.number),
	windDirection: PropTypes.number,
	zIndex: PropTypes.number,
	frostVignette: PropTypes.bool,
};

SnowEffect.displayName = 'SnowEffect';

export default SnowEffect;